import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card.jsx';
import { Button } from '../../components/ui/button.jsx';
import { Input } from '../../components/ui/input.jsx';
import { Label } from '../../components/ui/label.jsx';
import { Separator } from '../../components/ui/separator.jsx';
import { useToast } from '@/hooks/use-toast.js';
import { useAuth, useSignIn } from '@clerk/clerk-react';
import { Alert } from '@/components/ui/alert.jsx';

import { PasswordInput } from '@/components/ui/password-input.jsx';
import { Loader2 } from 'lucide-react';

const ForgotPassword = () => {
    const [email, setEmail] = useState('');
    const [code, setCode] = useState(Array(6).fill('')); // Array for individual inputs
    const [password, setPassword] = useState('');
    const [step, setStep] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const { toast } = useToast();
    const { isSignedIn, isLoaded } = useAuth();
    const { signIn, setActive } = useSignIn();
    const navigate = useNavigate();
    const inputRefs = useRef([]);

    const isAuthenticated = isSignedIn && isLoaded;

    const handleRequestReset = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setErrorMessage('');

        try {
            await signIn.create({
                strategy: 'reset_password_email_code',
                identifier: email,
            });

            setStep(2);
            setEmail('');
            toast({
                title: 'Check Your Email',
                description: 'A password reset code has been sent to your email.',
                variant: 'success',
            });
        } catch (err) {
            console.error(err);
            toast({
                title: 'Error',
                description: err?.message || 'Something went wrong. Try again.',
                variant: 'destructive',
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setErrorMessage('');

        try {
            const result = await signIn.attemptFirstFactor({
                strategy: 'reset_password_email_code',
                code: code.join(''), // Join array to string
                password,
            });

            if (result.status === 'complete') {
                setActive({ session: result.createdSessionId });
                toast({
                    title: 'Success',
                    description: 'Your password has been reset successfully.',
                    variant: 'success',
                });
                navigate('/');
            }
        } catch (err) {
            console.error(err.message);
            setErrorMessage(err.message || 'Invalid code or password.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCodeChange = (index, e) => {
        const value = e.target.value.replace(/\D/g, ''); // Only allow numbers
        if (value.length > 1) return;

        const newCode = [...code];
        newCode[index] = value;
        setCode(newCode);

        if (value && index < 5) {
            inputRefs.current[index + 1]?.focus(); // Move to next input
        }
    };

    const handleCodeKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !code[index] && index > 0) {
            inputRefs.current[index - 1]?.focus(); // Move to previous input
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').replace(/\D/g, ''); // Only numeric characters
        if (pastedData.length === 6) {
            setCode(pastedData.split('')); // Fill inputs
            pastedData.split('').forEach((char, i) => {
                inputRefs.current[i].value = char; // Set value visually
            });
            inputRefs.current[5]?.focus(); // Move focus to last field
        }
    };

    useEffect(() => {
        if (isAuthenticated) {
            navigate('/');
        }
    }, [isAuthenticated, navigate]);

    if (!isLoaded) {
        return (
            <div className="fixed inset-0 flex items-center justify-center bg-background">
                <div className="flex flex-col items-center space-y-4">
                    <Loader2 className="h-12 w-12 animate-spin text-primary" />
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-full min-w-full flex justify-center items-center">
            <Card className="w-full max-w-md p-6">
                {step === 1 && <CardHeader>
                    <CardTitle>
                        Forgot Password
                    </CardTitle>
                    <CardDescription>
                        Enter your email to receive a password reset code.
                    </CardDescription>
                </CardHeader>}

                <CardContent>
                    {step === 1 ? (
                        <form onSubmit={handleRequestReset}>
                            <div className="mb-4">
                                <Label htmlFor="email">Email</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="Enter your email"
                                    required
                                    disabled={isLoading}
                                />
                            </div>
                            <Separator className="my-4" />
                            <Button type="submit" className="w-full" disabled={isLoading}>
                                {isLoading ? 'Sending...' : 'Send Reset Code'}
                            </Button>
                        </form>
                    ) : (
                        <form onSubmit={handleResetPassword} autoComplete='off'>
                            {errorMessage && (
                                <Alert variant="destructive" className="mb-4">
                                    <span>{errorMessage}</span>
                                </Alert>
                            )}
                            <div className="mb-4">
                                <Label htmlFor="code">Reset Code</Label>
                                <CardDescription className="text-sm text-gray-500">
                                    Check your email for the one-time code. This may be in your spam or trash folder.
                                </CardDescription>
                                <div className="flex gap-2 mt-2" onPaste={handlePaste}>
                                    {Array(6)
                                        .fill("")
                                        .map((_, i) => (
                                            <Input
                                                key={i}
                                                ref={(el) => (inputRefs.current[i] = el)}
                                                type="text"
                                                value={code[i] || ""}
                                                onChange={(e) => handleCodeChange(i, e)}
                                                onKeyDown={(e) => handleCodeKeyDown(i, e)}
                                                maxLength={1}
                                                disabled={isLoading}
                                                inputMode="numeric"
                                                autoComplete="off"
                                                className="w-12 h-12 text-center text-lg border rounded-md focus:ring-2 focus:ring-primary"
                                                data-1p-ignore
                                            />
                                        ))}
                                </div>
                            </div>
                            <div className="mb-4">
                                <Separator className="my-4" />
                                <PasswordInput
                                    required
                                    pattern="^(?=.*[A-Z])(?=.*[^A-Za-z0-9]).{8,}$"
                                    title="Must have at least 8 characters, one uppercase letter, and one special character."
                                    value={password}
                                    name="new-password" // Use a unique name
                                    id="new-password"
                                    label='New Password'
                                    autoComplete="new-password"
                                    onChange={(e) => setPassword(e.target.value)}
                                />
                            </div>
                            <Separator className="my-4" />
                            <Button type="submit" className="w-full" disabled={isLoading}>
                                {isLoading ? 'Resetting...' : 'Reset Password'}
                            </Button>
                        </form>
                    )}

                    <div className="mt-4 space-y-2">
                        {step === 1 ? (
                            <>
                                <p>
                                    Remember your password?{' '}
                                    <Link className="text-blue-500" to="/login">
                                        Login
                                    </Link>
                                </p>
                                <p>
                                    Don't have an account?{' '}
                                    <Link className="text-blue-500" to="/signup">
                                        Sign up
                                    </Link>
                                </p>
                            </>
                        ) : (
                            <Button variant="link" onClick={() => setStep(1)}>
                                Go back
                            </Button>
                        )}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default ForgotPassword;
